import React, { useState, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const PRESET_MEDICATIONS = [
    {
        name: 'Amoxicilina',
        presentation: 'Cápsulas / Tabletas 500 mg',
        quantity: '# 21 cápsulas',
        dosage: 'Tomar 1 cápsula cada 8 horas',
        duration: 'Por 7 días',
        instructions: 'Tomar con un vaso lleno de agua después de los alimentos. No interrumpir el tratamiento.'
    },
    {
        name: 'Amoxicilina + Ácido Clavulánico',
        presentation: 'Comprimidos 875/125 mg',
        quantity: '# 14 comprimidos',
        dosage: 'Tomar 1 comprimido cada 12 horas',
        duration: 'Por 7 días',
        instructions: 'Tomar al inicio de una comida para reducir molestias gastrointestinales.'
    },
    {
        name: 'Ibuprofeno',
        presentation: 'Tabletas 600 mg',
        quantity: '# 10 tabletas',
        dosage: 'Tomar 1 tableta cada 8 horas',
        duration: 'Por 3 a 4 días',
        instructions: 'Tomar con alimentos. Para dolor e inflamación.'
    },
    {
        name: 'Ketorolaco',
        presentation: 'Tabletas Sublinguales 10 mg',
        quantity: '# 6 tabletas',
        dosage: 'Colocar 1 tableta debajo de la lengua cada 8 horas',
        duration: 'Máximo 3 días',
        instructions: 'Disolver bajo la lengua en caso de dolor moderado a severo.'
    },
    {
        name: 'Paracetamol',
        presentation: 'Tabletas 500 mg',
        quantity: '# 10 tabletas',
        dosage: 'Tomar 1 tableta cada 8 horas',
        duration: 'Por 3 días o según dolor',
        instructions: 'Tomar en caso de dolor leve o elevación de temperatura.'
    },
    {
        name: 'Clorhexidina al 0.12%',
        presentation: 'Frasco enjuague bucal 120 ml',
        quantity: '# 1 frasco',
        dosage: 'Realizar buches con 15 ml durante 1 minuto cada 12 horas',
        duration: 'Por 7 a 10 días',
        instructions: 'Usar después del cepillado dental. No ingerir alimentos ni bebidas por 30 minutos tras el uso.'
    },
    {
        name: 'Dexametasona',
        presentation: 'Tabletas 4 mg',
        quantity: '# 2 tabletas',
        dosage: 'Tomar 1 tableta cada 24 horas',
        duration: 'Por 2 días',
        instructions: 'Antiinflamatorio potente post-quirúrgico. Tomar con el desayuno.'
    },
    {
        name: 'Azitromicina',
        presentation: 'Tabletas 500 mg',
        quantity: '# 3 tabletas',
        dosage: 'Tomar 1 tableta cada 24 horas',
        duration: 'Por 3 días',
        instructions: 'Tomar 1 hora antes o 2 horas después de las comidas.'
    }
];

const ModalReceta = ({ isOpen, patient, onClose, onSaveSuccess }) => {
    const printAreaRef = useRef(null);
    const [activeTab, setActiveTab] = useState('nueva'); // 'nueva', 'historial', 'preview'
    const [selectedHistoryPrescription, setSelectedHistoryPrescription] = useState(null);

    // Form fields
    const [doctorName, setDoctorName] = useState('Dra. Silvana Ginnelle Estrada Castro');
    const [doctorReg, setDoctorReg] = useState('Reg. Senescyt: 1019-2021-2292400 | C.I. 0401908850');
    const [doctorSpecialty, setDoctorSpecialty] = useState('Odontología General y Especializada');
    const [recipeDate, setRecipeDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [diagnosis, setDiagnosis] = useState('');
    const [generalInstructions, setGeneralInstructions] = useState('• Tomar la medicación exactamente en las dosis y horarios prescritos.\n• No consumir bebidas alcohólicas durante el tratamiento farmacológico.\n• En caso de cualquier reacción alérgica o efecto adverso, suspender y comunicarse de inmediato.\n• Mantener una adecuada higiene bucal suave en la zona intervenida.');
    const [nextControl, setNextControl] = useState('Control en 7 días para retiro de puntos y evaluación clínica');
    const [medications, setMedications] = useState([
        { name: '', presentation: '', quantity: '', dosage: '', duration: '', instructions: '' }
    ]);

    const [isSaving, setIsSaving] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [statusMessage, setStatusMessage] = useState(null);

    const existingPrescriptions = (patient?.prescriptions && Array.isArray(patient.prescriptions)) 
        ? patient.prescriptions 
        : [];

    useEffect(() => {
        if (isOpen && patient) {
            // Reset to default new form or pick last diagnosis if available
            setRecipeDate(new Date().toISOString().split('T')[0]);
            setDiagnosis(patient.reason_for_consultation || '');
            if (existingPrescriptions.length > 0) {
                // If patient already has prescriptions, show history by default or stay on nueva
                setActiveTab('historial');
                setSelectedHistoryPrescription(existingPrescriptions[0]);
            } else {
                setActiveTab('nueva');
                setSelectedHistoryPrescription(null);
                setMedications([{ name: '', presentation: '', quantity: '', dosage: '', duration: '', instructions: '' }]);
            }
        }
    }, [isOpen, patient]);

    if (!isOpen || !patient) return null;

    const patientFullName = `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || 'Paciente sin nombre';
    const patientCedula = patient.cedula || 'Sin Cédula';
    const patientAge = patient.age ? `${patient.age} años` : (patient.birth_date ? `Nac: ${patient.birth_date}` : 'N/D');

    // Add empty medication row
    const handleAddMedication = () => {
        setMedications(prev => [
            ...prev,
            { name: '', presentation: '', quantity: '', dosage: '', duration: '', instructions: '' }
        ]);
    };

    // Remove medication row
    const handleRemoveMedication = (index) => {
        setMedications(prev => {
            const updated = [...prev];
            updated.splice(index, 1);
            return updated.length > 0 ? updated : [{ name: '', presentation: '', quantity: '', dosage: '', duration: '', instructions: '' }];
        });
    };

    // Update single medication field
    const handleMedicationChange = (index, field, value) => {
        setMedications(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    // Quick add preset
    const handleAddPreset = (preset) => {
        setMedications(prev => {
            // If the first row is completely empty, replace it
            if (prev.length === 1 && !prev[0].name.trim() && !prev[0].presentation.trim()) {
                return [{ ...preset }];
            }
            return [...prev, { ...preset }];
        });
    };

    // Load an existing prescription into the edit/new form
    const handleLoadPrescriptionToForm = (prescription) => {
        setRecipeDate(prescription.date || new Date().toISOString().split('T')[0]);
        setDiagnosis(prescription.diagnosis || '');
        setDoctorName(prescription.doctor_name || 'Dra. Silvana Ginnelle Estrada Castro');
        setDoctorReg(prescription.doctor_reg || 'Reg. Senescyt: 1019-2021-2292400 | C.I. 0401908850');
        setGeneralInstructions(prescription.general_instructions || '');
        setNextControl(prescription.next_control || '');
        if (prescription.medications && Array.isArray(prescription.medications) && prescription.medications.length > 0) {
            setMedications(prescription.medications);
        }
        setActiveTab('nueva');
    };

    // Save prescription to patient record
    const handleSavePrescription = async (andPrint = false) => {
        const validMeds = medications.filter(m => m.name.trim() !== '');
        if (validMeds.length === 0) {
            alert('Por favor ingrese al menos el nombre de un medicamento.');
            return;
        }

        setIsSaving(true);
        setStatusMessage(null);

        const newPrescriptionItem = {
            id: 'rec_' + Date.now(),
            created_at: new Date().toISOString(),
            date: recipeDate,
            doctor_name: doctorName,
            doctor_reg: doctorReg,
            doctor_specialty: doctorSpecialty,
            diagnosis: diagnosis.trim(),
            medications: validMeds,
            general_instructions: generalInstructions.trim(),
            next_control: nextControl.trim()
        };

        const updatedPrescriptions = [newPrescriptionItem, ...existingPrescriptions];

        // If category is not receta, auto update category to receta so it shows in the "Pacientes con Receta" tab
        const updatePayload = {
            prescriptions: updatedPrescriptions
        };
        if (patient.category !== 'receta' && patient.is_clinical !== false) {
            updatePayload.category = 'receta';
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/patients/${patient.id}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(updatePayload)
            });

            if (response.ok) {
                const updatedPatientData = await response.json();
                setStatusMessage({ type: 'success', text: '¡Receta guardada exitosamente!' });
                setSelectedHistoryPrescription(newPrescriptionItem);
                
                if (onSaveSuccess) {
                    onSaveSuccess({ ...patient, ...updatedPatientData, prescriptions: updatedPrescriptions });
                }

                if (andPrint) {
                    setTimeout(() => {
                        handlePrintPDF(newPrescriptionItem);
                    }, 400);
                } else {
                    setTimeout(() => {
                        setActiveTab('historial');
                        setStatusMessage(null);
                    }, 1200);
                }
            } else {
                setStatusMessage({ type: 'danger', text: 'Error al guardar la receta en el servidor.' });
            }
        } catch (error) {
            console.error('Error saving prescription:', error);
            setStatusMessage({ type: 'danger', text: 'Error de conexión al guardar receta.' });
        } finally {
            setIsSaving(false);
        }
    };

    // Delete prescription
    const handleDeletePrescription = async (prescriptionId) => {
        if (!window.confirm('¿Está seguro de que desea eliminar esta receta del historial?')) {
            return;
        }

        const filtered = existingPrescriptions.filter(p => p.id !== prescriptionId);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/patients/${patient.id}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ prescriptions: filtered })
            });

            if (response.ok) {
                if (onSaveSuccess) {
                    onSaveSuccess({ ...patient, prescriptions: filtered });
                }
                if (selectedHistoryPrescription?.id === prescriptionId) {
                    setSelectedHistoryPrescription(filtered[0] || null);
                }
            } else {
                alert('No se pudo eliminar la receta.');
            }
        } catch (e) {
            console.error(e);
            alert('Error al eliminar receta.');
        }
    };

    // Print / Export PDF
    const handlePrintPDF = async (targetPrescription = null) => {
        const itemToPrint = targetPrescription || selectedHistoryPrescription || {
            date: recipeDate,
            doctor_name: doctorName,
            doctor_reg: doctorReg,
            doctor_specialty: doctorSpecialty,
            diagnosis,
            medications: medications.filter(m => m.name.trim() !== ''),
            general_instructions: generalInstructions,
            next_control: nextControl
        };

        setSelectedHistoryPrescription(itemToPrint);
        setIsPrinting(true);

        try {
            const html2pdf = (await import('html2pdf.js')).default;
            const element = document.getElementById('printable-receta-sheet');
            if (!element) {
                alert('No se encontró el formato para imprimir.');
                setIsPrinting(false);
                return;
            }

            const opt = {
                margin: [10, 10, 10, 10],
                filename: `Receta_${patientFullName.replace(/\s+/g, '_')}_${itemToPrint.date || 'hoy'}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, logging: false },
                jsPDF: { unit: 'mm', format: 'a5', orientation: 'portrait' }
            };

            await html2pdf().set(opt).from(element).save();
        } catch (err) {
            console.error('Error al generar PDF de receta:', err);
            alert('Hubo un error al generar el PDF de la receta.');
        } finally {
            setIsPrinting(false);
        }
    };

    const currentPrintItem = selectedHistoryPrescription || {
        date: recipeDate,
        doctor_name: doctorName,
        doctor_reg: doctorReg,
        doctor_specialty: doctorSpecialty,
        diagnosis,
        medications: medications.filter(m => m.name.trim() !== ''),
        general_instructions: generalInstructions,
        next_control: nextControl
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '16px',
            backdropFilter: 'blur(4px)'
        }}>
            <div style={{
                width: '100%',
                maxWidth: '900px',
                maxHeight: '94vh',
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.4)',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <div style={{
                    padding: '18px 24px',
                    backgroundColor: '#002b5e',
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div className="d-flex align-items-center gap-3">
                        <div style={{
                            width: '42px',
                            height: '42px',
                            borderRadius: '10px',
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '22px'
                        }}>
                            <i className="bi bi-prescription2 text-warning"></i>
                        </div>
                        <div>
                            <h5 style={{ margin: 0, fontWeight: '800', fontSize: '1.2rem', letterSpacing: '-0.3px' }}>
                                Receta Médica Odontológica
                            </h5>
                            <small style={{ color: '#cbd5e1', fontSize: '12px' }}>
                                Paciente: <strong style={{ color: '#ffffff' }}>{patientFullName}</strong> • Cédula: <strong>{patientCedula}</strong> • Edad: <strong>{patientAge}</strong>
                            </small>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            fontSize: '1.8rem',
                            lineHeight: 1,
                            padding: '0 8px',
                            cursor: 'pointer',
                            opacity: 0.85
                        }}
                        title="Cerrar"
                    >
                        &times;
                    </button>
                </div>

                {/* Tabs navigation */}
                <div style={{
                    display: 'flex',
                    borderBottom: '1px solid #e2e8f0',
                    backgroundColor: '#f8fafc',
                    padding: '0 24px'
                }}>
                    <button
                        type="button"
                        onClick={() => setActiveTab('nueva')}
                        style={{
                            padding: '12px 20px',
                            border: 'none',
                            borderBottom: activeTab === 'nueva' ? '3px solid #002b5e' : '3px solid transparent',
                            background: 'transparent',
                            fontWeight: activeTab === 'nueva' ? '700' : '600',
                            color: activeTab === 'nueva' ? '#002b5e' : '#64748b',
                            fontSize: '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <i className="bi bi-plus-circle-fill"></i>
                        Crear / Nueva Receta
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('historial')}
                        style={{
                            padding: '12px 20px',
                            border: 'none',
                            borderBottom: activeTab === 'historial' ? '3px solid #002b5e' : '3px solid transparent',
                            background: 'transparent',
                            fontWeight: activeTab === 'historial' ? '700' : '600',
                            color: activeTab === 'historial' ? '#002b5e' : '#64748b',
                            fontSize: '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <i className="bi bi-journal-medical"></i>
                        Historial de Recetas
                        <span className="badge rounded-pill" style={{
                            backgroundColor: existingPrescriptions.length > 0 ? '#002b5e' : '#94a3b8',
                            color: 'white',
                            fontSize: '11px',
                            padding: '3px 8px'
                        }}>
                            {existingPrescriptions.length}
                        </span>
                    </button>
                </div>

                {/* Body Content */}
                <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, backgroundColor: '#fdfdfd' }}>
                    {statusMessage && (
                        <div className={`alert alert-${statusMessage.type} py-2 px-3 mb-3 d-flex align-items-center justify-content-between`} style={{ borderRadius: '8px', fontSize: '13px' }}>
                            <span>{statusMessage.text}</span>
                            <button type="button" className="btn-close btn-sm" onClick={() => setStatusMessage(null)}></button>
                        </div>
                    )}

                    {/* TAB: NUEVA RECETA */}
                    {activeTab === 'nueva' && (
                        <div>
                            {/* Doctor & Date Row */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold text-muted mb-1">Profesional Odontólogo</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={doctorName}
                                        onChange={(e) => setDoctorName(e.target.value)}
                                        placeholder="Nombre del odontólogo"
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small fw-bold text-muted mb-1">Registro / Cédula</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={doctorReg}
                                        onChange={(e) => setDoctorReg(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small fw-bold text-muted mb-1">Fecha Emisión</label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={recipeDate}
                                        onChange={(e) => setRecipeDate(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Diagnosis Row */}
                            <div className="mb-3">
                                <label className="form-label small fw-bold text-muted mb-1">Diagnóstico / Motivo de la Prescripción</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={diagnosis}
                                    onChange={(e) => setDiagnosis(e.target.value)}
                                    placeholder="Ej: Infección odontogénica / Pulpitis aguda irreversible / Post-exodoncia compleja"
                                />
                            </div>

                            {/* Quick Presets Chips */}
                            <div className="mb-3 p-3" style={{ backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <i className="bi bi-lightning-charge-fill text-warning"></i>
                                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#002b5e', textTransform: 'uppercase' }}>
                                        Medicamentos frecuentes (1 Clic para agregar)
                                    </span>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    {PRESET_MEDICATIONS.map((preset, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            onClick={() => handleAddPreset(preset)}
                                            className="btn btn-sm"
                                            style={{
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #cbd5e1',
                                                color: '#002b5e',
                                                borderRadius: '20px',
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                padding: '4px 10px',
                                                transition: 'all 0.15s ease'
                                            }}
                                            title={`Agregar ${preset.name} ${preset.presentation}`}
                                        >
                                            + {preset.name} <span style={{ opacity: 0.7 }}>({preset.presentation.split(' ')[1] || ''})</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Medications List */}
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 style={{ margin: 0, fontWeight: '800', color: '#002b5e', fontSize: '14px' }}>
                                        <i className="bi bi-capsule-pill me-1 text-primary"></i> Fármacos Prescritos (Rp.)
                                    </h6>
                                    <button
                                        type="button"
                                        onClick={handleAddMedication}
                                        className="btn btn-sm btn-outline-primary"
                                        style={{ borderRadius: '6px', fontSize: '12px', fontWeight: '700' }}
                                    >
                                        <i className="bi bi-plus-lg me-1"></i> Añadir Medicamento
                                    </button>
                                </div>

                                <div className="d-flex flex-column gap-2">
                                    {medications.map((med, idx) => (
                                        <div key={idx} style={{
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '10px',
                                            padding: '12px',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                                        }}>
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <span className="badge" style={{ backgroundColor: '#002b5e', color: 'white', fontSize: '11px' }}>
                                                    #{idx + 1}
                                                </span>
                                                {medications.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveMedication(idx)}
                                                        className="btn btn-sm text-danger p-0 border-0"
                                                        title="Eliminar este medicamento"
                                                    >
                                                        <i className="bi bi-trash"></i> Eliminar
                                                    </button>
                                                )}
                                            </div>

                                            <div className="row g-2 mb-2">
                                                <div className="col-md-5">
                                                    <label className="form-label" style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '2px' }}>
                                                        Medicamento (Principio Activo / Comercial) *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={med.name}
                                                        onChange={(e) => handleMedicationChange(idx, 'name', e.target.value)}
                                                        placeholder="Ej: Amoxicilina / Ketorolaco"
                                                    />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label" style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '2px' }}>
                                                        Presentación / Concentración
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={med.presentation}
                                                        onChange={(e) => handleMedicationChange(idx, 'presentation', e.target.value)}
                                                        placeholder="Ej: Tabletas 500mg / Jarabe"
                                                    />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label" style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '2px' }}>
                                                        Cantidad a despachar
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={med.quantity}
                                                        onChange={(e) => handleMedicationChange(idx, 'quantity', e.target.value)}
                                                        placeholder="Ej: # 21 tabletas / 1 frasco"
                                                    />
                                                </div>
                                            </div>

                                            <div className="row g-2">
                                                <div className="col-md-4">
                                                    <label className="form-label" style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '2px' }}>
                                                        Dosis y Frecuencia
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={med.dosage}
                                                        onChange={(e) => handleMedicationChange(idx, 'dosage', e.target.value)}
                                                        placeholder="Ej: 1 tableta cada 8 horas"
                                                    />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label" style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '2px' }}>
                                                        Duración
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={med.duration}
                                                        onChange={(e) => handleMedicationChange(idx, 'duration', e.target.value)}
                                                        placeholder="Ej: Por 7 días"
                                                    />
                                                </div>
                                                <div className="col-md-5">
                                                    <label className="form-label" style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '2px' }}>
                                                        Indicaciones Específicas / Modo de toma
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={med.instructions}
                                                        onChange={(e) => handleMedicationChange(idx, 'instructions', e.target.value)}
                                                        placeholder="Ej: Con las comidas / en ayunas"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* General Instructions & Next Control */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-8">
                                    <label className="form-label small fw-bold text-muted mb-1">
                                        Indicaciones Generales y Cuidados del Paciente
                                    </label>
                                    <textarea
                                        rows={3}
                                        className="form-control form-control-sm"
                                        value={generalInstructions}
                                        onChange={(e) => setGeneralInstructions(e.target.value)}
                                        placeholder="Indicaciones adicionales de reposo, alimentación, etc."
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold text-muted mb-1">
                                        Próximo Control / Cita
                                    </label>
                                    <textarea
                                        rows={3}
                                        className="form-control form-control-sm"
                                        value={nextControl}
                                        onChange={(e) => setNextControl(e.target.value)}
                                        placeholder="Ej: Control en 7 días"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB: HISTORIAL DE RECETAS */}
                    {activeTab === 'historial' && (
                        <div>
                            {existingPrescriptions.length === 0 ? (
                                <div className="text-center py-5">
                                    <div style={{ fontSize: '48px', color: '#cbd5e1', marginBottom: '12px' }}>
                                        <i className="bi bi-file-earmark-medical"></i>
                                    </div>
                                    <h6 style={{ color: '#64748b', fontWeight: '700' }}>No hay recetas registradas para este paciente</h6>
                                    <p style={{ color: '#94a3b8', fontSize: '13px' }}>
                                        Haz clic en "Crear / Nueva Receta" para emitir la primera receta médica odontológica.
                                    </p>
                                    <button
                                        type="button"
                                        className="btn btn-sm"
                                        onClick={() => setActiveTab('nueva')}
                                        style={{ backgroundColor: '#0d9488', color: 'white', borderRadius: '8px', fontWeight: '700', padding: '8px 18px' }}
                                    >
                                        <i className="bi bi-plus-lg me-1"></i> Crear Nueva Receta
                                    </button>
                                </div>
                            ) : (
                                <div className="row g-3">
                                    {/* Prescription List Column */}
                                    <div className="col-md-5">
                                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>
                                            Recetas Emitidas ({existingPrescriptions.length})
                                        </div>
                                        <div className="d-flex flex-column gap-2" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                                            {existingPrescriptions.map((p, idx) => {
                                                const isSelected = selectedHistoryPrescription?.id === p.id;
                                                return (
                                                    <div
                                                        key={p.id || idx}
                                                        onClick={() => setSelectedHistoryPrescription(p)}
                                                        style={{
                                                            padding: '12px 14px',
                                                            borderRadius: '10px',
                                                            backgroundColor: isSelected ? '#f1f5f9' : '#ffffff',
                                                            border: isSelected ? '2px solid #002b5e' : '1px solid #e2e8f0',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.15s ease'
                                                        }}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <span style={{ fontWeight: '800', color: '#002b5e', fontSize: '13px' }}>
                                                                <i className="bi bi-calendar3 me-1 text-primary"></i>
                                                                {p.date || 'Sin fecha'}
                                                            </span>
                                                            <span className="badge rounded-pill" style={{ backgroundColor: '#e2e8f0', color: '#475569', fontSize: '10px' }}>
                                                                {p.medications?.length || 0} medicamentos
                                                            </span>
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#334155', fontWeight: '600', marginBottom: '4px' }}>
                                                            {p.diagnosis || 'Consulta odontológica'}
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#64748b', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                            {p.medications?.map(m => m.name).join(', ') || 'Sin detalle'}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Selected Prescription Detail / Preview Column */}
                                    <div className="col-md-7">
                                        {selectedHistoryPrescription ? (
                                            <div style={{
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '12px',
                                                padding: '18px',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}>
                                                <div className="d-flex justify-content-between align-items-start mb-3 pb-2 border-bottom">
                                                    <div>
                                                        <div style={{ fontWeight: '800', color: '#002b5e', fontSize: '15px' }}>
                                                            Detalles de la Receta
                                                        </div>
                                                        <small className="text-muted">
                                                            Fecha: <strong>{selectedHistoryPrescription.date}</strong> • Dr(a): {selectedHistoryPrescription.doctor_name}
                                                        </small>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => handlePrintPDF(selectedHistoryPrescription)}
                                                            disabled={isPrinting}
                                                            className="btn btn-sm btn-primary"
                                                            style={{ backgroundColor: '#002b5e', borderColor: '#002b5e', borderRadius: '6px', fontWeight: '700', fontSize: '12px' }}
                                                            title="Imprimir o Descargar PDF"
                                                        >
                                                            <i className="bi bi-printer-fill me-1"></i> Imprimir PDF
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleLoadPrescriptionToForm(selectedHistoryPrescription)}
                                                            className="btn btn-sm btn-outline-secondary"
                                                            style={{ borderRadius: '6px', fontSize: '12px' }}
                                                            title="Cargar estos datos en el formulario para editar o re-emitir"
                                                        >
                                                            <i className="bi bi-arrow-repeat"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeletePrescription(selectedHistoryPrescription.id)}
                                                            className="btn btn-sm btn-outline-danger"
                                                            style={{ borderRadius: '6px', fontSize: '12px' }}
                                                            title="Eliminar del historial"
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>

                                                <div style={{ flex: 1, overflowY: 'auto' }}>
                                                    {selectedHistoryPrescription.diagnosis && (
                                                        <div className="mb-2">
                                                            <span className="text-muted small fw-bold">Diagnóstico: </span>
                                                            <span style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>
                                                                {selectedHistoryPrescription.diagnosis}
                                                            </span>
                                                        </div>
                                                    )}

                                                    <div className="mb-3">
                                                        <span className="text-muted small fw-bold d-block mb-1">Medicamentos:</span>
                                                        <div className="d-flex flex-column gap-2">
                                                            {selectedHistoryPrescription.medications?.map((m, idx) => (
                                                                <div key={idx} style={{ backgroundColor: '#f8fafc', padding: '10px 12px', borderRadius: '8px', borderLeft: '3px solid #002b5e' }}>
                                                                    <div className="d-flex justify-content-between">
                                                                        <strong style={{ color: '#002b5e', fontSize: '13px' }}>
                                                                            {idx + 1}. {m.name} {m.presentation && `(${m.presentation})`}
                                                                        </strong>
                                                                        {m.quantity && <span className="badge bg-light text-dark border">{m.quantity}</span>}
                                                                    </div>
                                                                    <div style={{ fontSize: '12px', color: '#334155', marginTop: '2px' }}>
                                                                        {m.dosage} {m.duration && `• ${m.duration}`}
                                                                    </div>
                                                                    {m.instructions && (
                                                                        <div style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic', marginTop: '2px' }}>
                                                                            {m.instructions}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {selectedHistoryPrescription.general_instructions && (
                                                        <div className="mb-2">
                                                            <span className="text-muted small fw-bold d-block">Indicaciones Generales:</span>
                                                            <p style={{ fontSize: '12px', color: '#334155', whiteSpace: 'pre-wrap', margin: 0, backgroundColor: '#fdfdfd', padding: '8px', borderRadius: '6px', border: '1px solid #f1f5f9' }}>
                                                                {selectedHistoryPrescription.general_instructions}
                                                            </p>
                                                        </div>
                                                    )}

                                                    {selectedHistoryPrescription.next_control && (
                                                        <div>
                                                            <span className="text-muted small fw-bold d-block">Próximo Control:</span>
                                                            <span style={{ fontSize: '12px', color: '#002b5e', fontWeight: '700' }}>
                                                                {selectedHistoryPrescription.next_control}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center h-100 text-muted small">
                                                Selecciona una receta del listado para ver su detalle
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer Modal Actions */}
                <div style={{
                    padding: '14px 24px',
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#ffffff'
                }}>
                    <div>
                        {activeTab === 'nueva' && (
                            <small className="text-muted">
                                * Al guardar, el paciente se clasifica automáticamente en <strong>Pacientes con Receta</strong>.
                            </small>
                        )}
                    </div>
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn btn-secondary px-4"
                            style={{ borderRadius: '8px', fontWeight: '600' }}
                            onClick={onClose}
                            disabled={isSaving || isPrinting}
                        >
                            Cerrar
                        </button>

                        {activeTab === 'nueva' && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-outline-primary px-3"
                                    style={{ borderRadius: '8px', fontWeight: '700' }}
                                    onClick={() => handleSavePrescription(false)}
                                    disabled={isSaving || isPrinting}
                                >
                                    <i className="bi bi-save me-1"></i> {isSaving ? 'Guardando...' : 'Guardar Receta'}
                                </button>
                                <button
                                    type="button"
                                    className="btn px-4"
                                    style={{ backgroundColor: '#002b5e', color: 'white', borderRadius: '8px', fontWeight: '700' }}
                                    onClick={() => handleSavePrescription(true)}
                                    disabled={isSaving || isPrinting}
                                >
                                    <i className="bi bi-printer-fill me-1"></i> {isPrinting ? 'Generando PDF...' : 'Guardar e Imprimir'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* PRINTABLE OFFICIAL RECETA (Invisible on screen, captured by html2pdf) */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
                <div id="printable-receta-sheet" style={{
                    width: '148mm', // A5 standard prescription format
                    minHeight: '210mm',
                    backgroundColor: '#ffffff',
                    padding: '16mm 14mm',
                    fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                    color: '#0f172a',
                    boxSizing: 'border-box',
                    position: 'relative'
                }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2.5px solid #002b5e', paddingBottom: '10px', marginBottom: '12px' }}>
                        <div>
                            <div style={{ fontSize: '20px', fontWeight: '900', color: '#002b5e', letterSpacing: '-0.5px' }}>
                                BIODENTALITY
                            </div>
                            <div style={{ fontSize: '9px', fontWeight: '800', color: '#002b5e', letterSpacing: '1px', textTransform: 'uppercase' }}>
                                Centro Odontológico Especializado
                            </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '11px', fontWeight: '800', color: '#002b5e' }}>
                                {currentPrintItem.doctor_name || 'Dra. Silvana Ginnelle Estrada Castro'}
                            </div>
                            <div style={{ fontSize: '8.5px', color: '#475569' }}>
                                {currentPrintItem.doctor_specialty || 'Odontología General y Especializada'}
                            </div>
                            <div style={{ fontSize: '8px', color: '#64748b' }}>
                                {currentPrintItem.doctor_reg || 'Reg. Senescyt: 1019-2021-2292400 • C.I. 0401908850'}
                            </div>
                            <div style={{ fontSize: '8px', color: '#64748b' }}>
                                Tulcán - Ecuador
                            </div>
                        </div>
                    </div>

                    {/* Patient Information Box */}
                    <div style={{
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        marginBottom: '14px',
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr',
                        rowGap: '4px',
                        columnGap: '8px',
                        fontSize: '9.5px'
                    }}>
                        <div>
                            <span style={{ color: '#64748b', fontWeight: '600' }}>Paciente: </span>
                            <strong style={{ color: '#0f172a' }}>{patientFullName}</strong>
                        </div>
                        <div>
                            <span style={{ color: '#64748b', fontWeight: '600' }}>Cédula: </span>
                            <strong style={{ color: '#0f172a' }}>{patientCedula}</strong>
                        </div>
                        <div>
                            <span style={{ color: '#64748b', fontWeight: '600' }}>Edad: </span>
                            <strong style={{ color: '#0f172a' }}>{patientAge}</strong>
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                            <span style={{ color: '#64748b', fontWeight: '600' }}>Diagnóstico: </span>
                            <strong style={{ color: '#0f172a' }}>{currentPrintItem.diagnosis || 'Atención Odontológica'}</strong>
                        </div>
                        <div>
                            <span style={{ color: '#64748b', fontWeight: '600' }}>Fecha: </span>
                            <strong style={{ color: '#0f172a' }}>{currentPrintItem.date || new Date().toISOString().split('T')[0]}</strong>
                        </div>
                    </div>

                    {/* Rp Symbol */}
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '26px', fontWeight: '900', fontFamily: 'serif', color: '#002b5e', fontStyle: 'italic', lineHeight: 1 }}>
                            Rp.
                        </span>
                        <span style={{ fontSize: '9px', fontWeight: '700', color: '#002b5e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Prescripción Farmacológica
                        </span>
                    </div>

                    {/* Prescribed Medications */}
                    <div style={{ marginBottom: '14px', minHeight: '65mm' }}>
                        {currentPrintItem.medications && currentPrintItem.medications.length > 0 ? (
                            currentPrintItem.medications.map((m, idx) => (
                                <div key={idx} style={{
                                    marginBottom: '8px',
                                    paddingBottom: '6px',
                                    borderBottom: '1px dashed #e2e8f0'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: '800', color: '#0f172a' }}>
                                        <span>{idx + 1}. {m.name} {m.presentation && `— ${m.presentation}`}</span>
                                        {m.quantity && <span style={{ color: '#002b5e' }}>({m.quantity})</span>}
                                    </div>
                                    <div style={{ fontSize: '9.5px', color: '#334155', marginTop: '2px', paddingLeft: '14px' }}>
                                        <strong style={{ color: '#002b5e' }}>Indicación: </strong> {m.dosage} {m.duration && `• ${m.duration}`}
                                    </div>
                                    {m.instructions && (
                                        <div style={{ fontSize: '8.5px', color: '#64748b', paddingLeft: '14px', fontStyle: 'italic', marginTop: '1px' }}>
                                            Nota: {m.instructions}
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div style={{ fontSize: '10px', color: '#94a3b8', fontStyle: 'italic' }}>
                                No se detallaron medicamentos específicos.
                            </div>
                        )}
                    </div>

                    {/* General Recommendations */}
                    {currentPrintItem.general_instructions && (
                        <div style={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '5px',
                            padding: '6px 8px',
                            marginBottom: '10px',
                            fontSize: '8.5px',
                            color: '#334155'
                        }}>
                            <div style={{ fontWeight: '800', color: '#002b5e', marginBottom: '2px', textTransform: 'uppercase', fontSize: '8px' }}>
                                Indicaciones y Cuidados Generales:
                            </div>
                            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.35 }}>
                                {currentPrintItem.general_instructions}
                            </div>
                        </div>
                    )}

                    {/* Next Appointment Control */}
                    {currentPrintItem.next_control && (
                        <div style={{ fontSize: '8.5px', color: '#002b5e', marginBottom: '14px', fontWeight: '700' }}>
                            Próximo Control: <span style={{ color: '#002b5e' }}>{currentPrintItem.next_control}</span>
                        </div>
                    )}

                    {/* Signature and Seal Area */}
                    <div style={{
                        marginTop: '20px',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        textAlign: 'center'
                    }}>
                        <div style={{ width: '60mm', textAlign: 'center' }}>
                            <div style={{ borderTop: '1px solid #475569', paddingTop: '4px', marginBottom: '2px' }}></div>
                            <div style={{ fontSize: '9.5px', fontWeight: '800', color: '#002b5e' }}>
                                {currentPrintItem.doctor_name || 'Dra. Silvana Ginnelle Estrada Castro'}
                            </div>
                            <div style={{ fontSize: '8px', color: '#64748b' }}>
                                Firma y Sello Profesional
                            </div>
                            <div style={{ fontSize: '7.5px', color: '#94a3b8' }}>
                                {currentPrintItem.doctor_reg || 'Reg. Senescyt: 1019-2021-2292400'}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div style={{
                        position: 'absolute',
                        bottom: '8mm',
                        left: '14mm',
                        right: '14mm',
                        borderTop: '1px solid #cbd5e1',
                        paddingTop: '4px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '7.5px',
                        color: '#94a3b8'
                    }}>
                        <span>Biodentality • Salud y Estética Dental</span>
                        <span>Tulcán, Ecuador</span>
                        <span>Válido según prescripción médica</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalReceta;
